/**
 * useSettings Hook
 * Manages user settings with backend synchronization.
 * Success/error messages are routed to the global ToastContext.
 */

import { useState, useEffect } from "react";
import { useLicenseContext } from "../contexts/LicenseContext";
import { useToast } from "../contexts/ToastContext";
import { translate } from "../i18n";
import {
  updateSettings as updateSettingsApi,
  lockSettings as lockSettingsApi,
  unlockSettings as unlockSettingsApi,
  resetSettings as resetSettingsApi,
  saveCustomTemplate as saveCustomTemplateApi,
  resetCustomTemplate as resetCustomTemplateApi
} from "../api/settingsApi";

export default function useSettings() {
  const { settings: initialSettings, oid, reloadLicenseStatus } = useLicenseContext();
  const { showToast } = useToast();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  const updateSetting = async (key, value) => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    setLoading(true);
    try {
      const updateDto = { ...settings, [key]: value };
      const result = await updateSettingsApi(oid, updateDto);
      if (!result.success) { showToast(result.message, "error"); return false; }
      setSettings({ ...settings, [key]: value });
      await reloadLicenseStatus();
      // Checkbox changes are silent — no toast for every toggle
      return true;
    } catch (err) {
      console.error("Error updating setting:", err);
      showToast(translate("Fehler beim Speichern der Einstellung"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateMultipleSettings = async (updates) => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    setLoading(true);
    try {
      const updateDto = { ...settings, ...updates };
      const result = await updateSettingsApi(oid, updateDto);
      if (!result.success) { showToast(result.message, "error"); return false; }
      setSettings({ ...settings, ...updates });
      await reloadLicenseStatus();
      return true;
    } catch (err) {
      console.error("Error updating settings:", err);
      showToast(translate("Fehler beim Speichern der Einstellungen"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const lockSettings = async (password, passwordConfirm) => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    setLoading(true);
    try {
      const result = await lockSettingsApi(oid, password, passwordConfirm);
      if (!result.success) { showToast(result.message, "error"); return false; }
      await reloadLicenseStatus();
      showToast(result.message || translate("Einstellungen wurden gesperrt."), "success");
      return true;
    } catch (err) {
      console.error("Error locking settings:", err);
      showToast(translate("Fehler beim Sperren der Einstellungen"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unlockSettings = async (password) => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    setLoading(true);
    try {
      const result = await unlockSettingsApi(oid, password);
      if (!result.success) { showToast(result.message, "error"); return false; }
      await reloadLicenseStatus();
      showToast(result.message || translate("Einstellungen wurden freigegeben."), "success");
      return true;
    } catch (err) {
      console.error("Error unlocking settings:", err);
      showToast(translate("Fehler beim Freigeben der Einstellungen"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetSettings = async () => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    setLoading(true);
    try {
      const result = await resetSettingsApi(oid);
      if (!result.success) { showToast(result.message, "error"); return false; }
      await reloadLicenseStatus();
      showToast(result.message || translate("Einstellungen wurden zurückgesetzt."), "success");
      return true;
    } catch (err) {
      console.error("Error resetting settings:", err);
      showToast(translate("Fehler beim Zurücksetzen der Einstellungen"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const saveCustomTemplate = async (htmlContent) => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    if (!htmlContent || htmlContent.trim() === "") {
      showToast(translate("Der E-Mail-Inhalt ist leer"), "error");
      return false;
    }
    setLoading(true);
    try {
      const result = await saveCustomTemplateApi(oid, htmlContent);
      if (!result.success) { showToast(result.message, "error"); return false; }
      await reloadLicenseStatus();
      showToast(result.message || translate("Vorlage wurde gespeichert."), "success");
      return true;
    } catch (err) {
      console.error("Error saving custom template:", err);
      showToast(translate("Fehler beim Speichern der Vorlage"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetCustomTemplate = async () => {
    if (!oid) { showToast(translate("OID nicht verfügbar"), "error"); return false; }
    setLoading(true);
    try {
      const result = await resetCustomTemplateApi(oid);
      if (!result.success) { showToast(result.message, "error"); return false; }
      await reloadLicenseStatus();
      showToast(result.message || translate("Vorlage wurde zurückgesetzt."), "success");
      return true;
    } catch (err) {
      console.error("Error resetting custom template:", err);
      showToast(translate("Fehler beim Zurücksetzen der Vorlage"), "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    settings,
    loading,
    updateSetting,
    updateMultipleSettings,
    lockSettings,
    unlockSettings,
    resetSettings,
    saveCustomTemplate,
    resetCustomTemplate,
    oid,
  };
}
